<script setup lang="ts">
import type { Ref } from 'vue';
import type { ReadonlyDeep } from 'type-fest';
import type { HeadersData } from '@/composables/useTable';

import { computed, reactive } from 'vue';

import { utcDateStrToUserZonedDateStrFormat } from '@/utils/format';
import TableActionBtn from '@/components/btns/TableActionBtn.vue';
import TableLoader from '@/components/loaders/TableLoader.vue';
import EmDashPlaceholder from '@/components/placeholders/EmDashPlaceholder.vue';
import SortThTableCell from '@/components/tableCells/SortThTableCell.vue';
import TableEmptyText from '@/components/texts/TableEmptyText.vue';
import useTable from '@/composables/useTable';
import { useUserStore } from '@/stores/useUserStore';
import { fetchGetReportFile } from '@/helpers/fetchers';
import { ReportResponse } from '@/models/reports';

import tableStyles from '@/styles/table.module.scss';

export interface Props {
  loading?: boolean;
  data?: ReadonlyDeep<ReportResponse[]>;
}
const props = withDefaults(defineProps<Props>(), {
  loading: false,
  data: () => [],
});

const formatPattern = 'hh:mm aa, MMM dd, yyyy';

const userStore = useUserStore();

const isDownloadingKpiReport = reactive<Record<number, boolean>>({});

const useTableValues = useTable(props);
const { actives, ascs, handleSort } = useTableValues;
const sortedData = useTableValues.sortedData as Readonly<
  Ref<ReadonlyDeep<ReportResponse[]>>
>;

const headersData = computed<HeadersData>(function () {
  // if (props.isStaff) { // use computed to keep the pattern from other tables
  return [
    {
      value: 'customer',
      title: 'Customer',
      isNotSortable: true,
    },
    {
      value: 'title',
      title: 'Report',
      isNotSortable: true,
    },
    {
      value: 'created_by',
      title: 'Created by',
    },
    {
      value: 'created_at',
      title: 'Created at',
    },
    {
      value: 'approved_at',
      title: 'Approved at',
    },
  ];
});

const foldColspan = computed(() => {
  return headersData.value.length + 1;
});

function downloadItem(id: number): void {
  if (isDownloadingKpiReport[id]) return;
  isDownloadingKpiReport[id] = true;
  void fetchGetReportFile(id, 'KPI').finally(() => {
    /* eslint-disable-next-line @typescript-eslint/no-dynamic-delete */
    delete isDownloadingKpiReport[id];
  });
}

const userTimeZone = computed(() => userStore.user.timezone);

function dateStrItemExt(value: unknown): string {
  return utcDateStrToUserZonedDateStrFormat(
    value,
    userTimeZone.value,
    formatPattern,
  );
}
</script>

<template>
  <table
    :class="[
      tableStyles.table,
      tableStyles.stickyHeader,
      $style.kpiReportTable,
    ]"
    data-testid="kpiReportTable"
  >
    <thead>
      <tr>
        <template
          v-for="(headerItem, headerIndex) in headersData"
          :key="headerIndex"
        >
          <SortThTableCell
            :colspan="headerItem.colspan"
            :actives="actives"
            :ascs="ascs"
            :handle-sort="handleSort"
            :header-item-value="headerItem.value"
            :is-not-sortable="headerItem.isNotSortable"
            :class="$style[headerItem.value + 'Th']"
          >
            {{ headerItem.title }}
          </SortThTableCell>
        </template>
        <th :class="$style.actionsTh">Actions</th>
      </tr>
    </thead>
    <tr v-if="props.loading">
      <td :colspan="foldColspan" class="!h-64 p-0">
        <TableLoader />
      </td>
    </tr>
    <tr v-else-if="!sortedData?.length">
      <td :colspan="foldColspan" class="border-b-0 p-0">
        <TableEmptyText />
      </td>
    </tr>
    <tbody v-else>
      <template v-for="dataItem in sortedData" :key="dataItem.unique_key">
        <tr>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder :item="dataItem.customer" />
          </td>
          <td class="w-100">
            <EmDashPlaceholder :item="dataItem.title" />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder :item="dataItem.created_by" />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder
              :item="dataItem.created_at"
              :ext="dateStrItemExt"
            />
          </td>
          <td class="whitespace-nowrap">
            <EmDashPlaceholder
              :item="dataItem.approved_at"
              :ext="dateStrItemExt"
            />
          </td>
          <td class="whitespace-nowrap">
            <div :class="$style.actionBtns">
              <TableActionBtn
                color="green"
                icon="mdi-download-outline"
                :loading="isDownloadingKpiReport[dataItem.id]"
                @click="downloadItem(dataItem.id)"
              >
                Download
              </TableActionBtn>
            </div>
          </td>
        </tr>
      </template>
    </tbody>
  </table>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

.kpiReportTable {
  width: 100%;

  & > thead > tr {
    & > th {
      white-space: nowrap;
    }
  }
}
  .actionBtns {
    margin-left: -#{variables.$tableActionBtnMarginX};
  }
</style>
